import { defineComponent as _defineComponent } from 'vue'
import moment, { Moment } from 'moment-timezone'
import { storeToRefs } from 'pinia'
import { computed, getCurrentInstance, ref, watch } from 'vue'

import AnalyticsChartVertical from '@/client/components/_atoms/analytics_chart_vertical'
import AnalyticsPeriodPicker from '@/client/components/_atoms/analytics_period_picker'
import AnalyticsTotal from '@/client/components/_atoms/analytics_total'
import Icon from '@/client/components/_atoms/basic_icon'
import CsvDownload from '@/client/components/_atoms/csv_download'
import { CAMPAIGN_STATUS } from '@/client/constant'
import Util from '@/client/core/util'
import { TrackingService } from '@/client/services'

import { useInstagramInstantwinCampaignAnalyticsStore } from '../hooks/instagramInstantwinCampaignAnalyticsStore'
import { Interval, MessageSendCountSortBy, MetricSelection } from '../types'
import InstagramInstantwinCampaignAnalyticsEnqueteTab from './InstagramInstantwinCampaignAnalyticsEnqueteTab.vue'
import InstagramInstantwinCampaignAnalyticsMessageSendCountsTab from './InstagramInstantwinCampaignAnalyticsMessageSendCountsTab.vue'

type DateRangeValue = [] | [string | Date | Moment, string | Date | Moment] | null


export default /*#__PURE__*/_defineComponent({
  __name: 'InstagramInstantwinCampaignAnalyticsDialog',
  setup(__props, { expose }) {

// @ts-nocheck TODO: 既存の型エラーを解消したら外す
const { proxy } = getCurrentInstance() as any

const visible = ref(false)

const activeTab = ref('campaigns')
const store = useInstagramInstantwinCampaignAnalyticsStore()
const {
  campaign,
  campaignMeta,
  analyticsRows,
  prizeOptions,
  prizeId,
  enqueteQuestions,
  enqueteSelectedId,
  enqueteAnswers,
  enqueteTotalCount,
  messageSendCountRows,
  messageSendCountTotal,
  messageSendCountPagingNo,
  messageSendCountLimitCount,
  messageSendCountSortBy,
  enqueteLoading,
  messageSendCountLoading,
  dateTimeRange,
  interval,
  value1,
  value2,
  isBeginAtZero,
  isPublicAnalyticsPage,
  isPublicAnalyticsApi,
  loading,
  labels,
  analytics,
  entryCount,
  applicantCount,
  newApplicantCount,
  winnerCount,
  followerUpDown
} = storeToRefs(store)

const metricOptions = [
  { label: '指定なし', value: '' },
  { label: '応募数', value: 'entry' },
  { label: '応募者数', value: 'applicant' },
  { label: '新規応募者数', value: 'newApplicant' },
  { label: '当選数', value: 'winner' },
  { label: 'フォロワー', value: 'follower' }
]

const intervals: Array<{ text: string; value: Interval }> = [
  { text: '1時間', value: 'hour' },
  { text: '1日', value: '1_day' },
  { text: '7日', value: '7_day' },
  { text: '1ヶ月', value: '1_month' }
]

const campaignName = computed(() => campaignMeta.value.name ?? '')
const campaignStartDatetime = computed(() => campaign.value?.startDatetime ?? null)
const campaignEndDatetime = computed(() => campaign.value?.endDatetime ?? null)

const isCampaignFinalCollection = computed(() => {
  return campaignMeta.value.status === CAMPAIGN_STATUS.DURING_FINAL_COLLECTION
})

const isCampaignCollectionCompleted = computed(() => {
  return campaignMeta.value.status === CAMPAIGN_STATUS.COLLECTION_COMPLETED
})

const collectCompletedAt = computed(() => {
  if (!campaignMeta.value.endDatetime) {
    return ''
  }

  // 分析バッチの実行完了を考慮し、キャンペーン終了時刻の1時間後を表示する
  return moment(campaignMeta.value.endDatetime).add(1, 'hours').format('YYYY年MM月DD日 HH:mm')
})

const hasAnalyticsData = computed(() => analyticsRows.value.length > 0)

const chartData = computed(() => {
  const data: Array<{ metric: string; label: string; data: Array<number | null> }> = []

  if (value1.value) {
    const selected = metricOptions.find(option => option.value === value1.value)
    if (selected) {
      data.push({
        metric: value1.value,
        label: selected.label,
        data: analytics.value[value1.value as keyof typeof analytics.value]
      })
    }
  }

  if (value2.value) {
    const selected = metricOptions.find(option => option.value === value2.value)
    if (selected) {
      data.push({
        metric: value2.value,
        label: selected.label,
        data: analytics.value[value2.value as keyof typeof analytics.value]
      })
    }
  }

  return data
})

const publicPageUrl = computed(() => {
  if (!campaignMeta.value.hash) {
    return ''
  }

  return `https://${window.location.host}/publics/instagram_instantwin_campaign_analytics?hash=${campaignMeta.value.hash}`
})

const publicApiUrl = computed(() => {
  if (!campaignMeta.value.hash) {
    return ''
  }

  const baseUrl = `https://${window.location.host}/api/publics/applicant_count?hash=${campaignMeta.value.hash}`
  if (prizeId.value) {
    return `${baseUrl}&prizeId=${prizeId.value}`
  }

  return baseUrl
})

const open = async (campaignId: number) => {
  visible.value = true
  activeTab.value = 'campaigns'
  const loaded = await store.initialize(campaignId)
  if (!loaded) {
    alert()
    close()
  }
}

const close = () => {
  visible.value = false
  store.reset()
}

const handleBeforeClose = () => {
  TrackingService.sendEvent('click:キャンペーン分析（IGIW）|閉じる')
  close()
}

const handleDateRangeChange = async (value: DateRangeValue) => {
  TrackingService.sendEvent('select:キャンペーン分析（IGIW）|カレンダー')

  store.setDateTimeRange(value)
  const loaded = await store.refreshAnalytics()
  if (!loaded) {
    alert()
    close()
  }
  await refreshEnqueteByTab(activeTab.value)
}

const handlePrizeChange = async (value: number | null) => {
  TrackingService.sendEvent('select:キャンペーン分析（IGIW）|全ての応募グループ')

  store.setPrizeId(value)
  const loaded = await store.refreshAnalytics()
  if (!loaded) {
    alert()
    close()
  }
  await refreshEnqueteByTab(activeTab.value)
}

const refreshEnqueteByTab = async (tabName: string) => {
  if (!prizeId.value) {
    return
  }
  if (tabName === 'questionnaire') {
    const loaded = await store.refreshEnquete()
    if (!loaded) {
      alert()
      close()
    }
  }
  if (tabName === 'message_send_counts') {
    const loaded = await store.refreshMessageSendCounts()
    if (!loaded) {
      alert()
      close()
    }
  }
}

const handleEnqueteQuestionChange = async (value: number) => {
  store.setEnqueteSelectedId(value)
  const loaded = await store.refreshEnquete()
  if (!loaded) {
    alert()
    close()
  }
}

const handleMessageSendCountsSort = async (sortBy: MessageSendCountSortBy | '') => {
  store.setMessageSendCountSortBy(sortBy)
  if (!prizeId.value) return
  const loaded = await store.refreshMessageSendCounts()
  if (!loaded) {
    alert()
    close()
  }
}

const handleMessageSendCountsPaging = async ({ currentPage, pageSize }: { currentPage: number; pageSize: number }) => {
  store.setMessageSendCountPaging(currentPage, pageSize)
  if (!prizeId.value) return
  const loaded = await store.refreshMessageSendCounts()
  if (!loaded) {
    alert()
    close()
  }
}

const handleMetricChange = (target: 'value1' | 'value2') => {
  if (target === 'value1') {
    TrackingService.sendEvent('select:キャンペーン分析（IGIW）|値1')
    store.setMetricSelection('value1', value1.value as MetricSelection)
    return
  }

  TrackingService.sendEvent('select:キャンペーン分析（IGIW）|値2')
  store.setMetricSelection('value2', value2.value as MetricSelection)
}

const handleIntervalChange = (value: Interval) => {
  if (value === 'hour') {
    TrackingService.sendEvent('click:キャンペーン分析（IGIW）|1時間')
  } else if (value === '1_day') {
    TrackingService.sendEvent('click:キャンペーン分析（IGIW）|1日')
  } else if (value === '7_day') {
    TrackingService.sendEvent('click:キャンペーン分析（IGIW）|7日')
  } else {
    TrackingService.sendEvent('click:キャンペーン分析（IGIW）|キャンペーン|1ヶ月')
  }

  store.setIntervalValue(value)
}

const handlePublicFlagsChange = async (label: string) => {
  TrackingService.sendEvent(
    `click:キャンペーン分析（IGIW）|${label === '分析結果の共有' ? '分析結果の共有' : 'APIとして公開'}`
  )

  const updated = await store.updatePublicFlags()
  if (!updated) {
    proxy.$notify({
      title: 'キャンペーン分析公開フラグの変更に失敗しました。',
      customClass: 'danger',
      duration: 5000
    })
  }
}

const downloadCsv = () => {
  TrackingService.sendEvent('click:キャンペーン分析（IGIW）|CSVダウンロード')

  const filename = `${campaignName.value}_分析`
  const data: Array<Array<string | number | null>> = []

  data.push(['日付', '応募数', '応募者数', '新規応募者数', '当選数', 'フォロワー'])

  labels.value.forEach((label, index) => {
    let resolvedLabel = label
    if (interval.value === '1_day') {
      resolvedLabel = moment(label).startOf('hour').format('YYYY/MM/DD')
    }

    const applicantValue = analytics.value.applicant[index]
    const followerValue = analytics.value.follower[index]
    const entryValue = analytics.value.entry[index]
    const newApplicantValue = analytics.value.newApplicant[index]
    const winnerValue = analytics.value.winner[index]

    const applicant = typeof applicantValue === 'number' && !isNaN(applicantValue) ? applicantValue : null
    const follower = typeof followerValue === 'number' && !isNaN(followerValue) ? followerValue : null
    const entry = typeof entryValue === 'number' && !isNaN(entryValue) ? entryValue : null
    const newApplicant = typeof newApplicantValue === 'number' && !isNaN(newApplicantValue) ? newApplicantValue : null
    const winner = typeof winnerValue === 'number' && !isNaN(winnerValue) ? winnerValue : null

    data.push([resolvedLabel, entry, applicant, newApplicant, winner, follower])
  })

  return { filename, data }
}

const downloadMessageSendCountsCsv = async () => {
  TrackingService.sendEvent('click:キャンペーン分析（IGIW）|メッセージごとの送信数|CSVダウンロード')

  if (!prizeId.value) {
    return { error: 'NO_DATA' }
  }

  const payload = await store.exportMessageSendCountsCsv()
  const rows = payload?.rows ?? []
  if (!rows.length) {
    return { error: 'NO_DATA' }
  }

  const data: Array<Array<string | number | null>> = []
  data.push(['メッセージ', '種別', '送信数'])
  rows.forEach(row => {
    data.push([row.message || '', row.messageType, row.receivedCount])
  })

  const filename = `${campaignName.value}_メッセージ送信数`
  return { filename, data }
}

const copyToClipboard = (text: string) => {
  Util.copyToClipboard(text)
  proxy.$message({
    message: 'クリップボードにURLをコピーしました。',
    type: 'success'
  })
}

const alert = () => {
  proxy.$notify({
    title: 'キャンペーン分析の取得に失敗しました。',
    message: '恐れ入りますが、時間をおいて再度お試しください。',
    customClass: 'danger',
    duration: 5000
  })
}

const handleBeginAtZeroChange = () => {
  TrackingService.sendEvent('switch:キャンペーン分析（IGIW）|0から表示')
  store.setBeginAtZero(Boolean(isBeginAtZero.value))
}

watch(activeTab, async value => {
  await refreshEnqueteByTab(value)
})

expose({
  open
})

return { __sfc: true,proxy, visible, activeTab, store, campaign, campaignMeta, analyticsRows, prizeOptions, prizeId, enqueteQuestions, enqueteSelectedId, enqueteAnswers, enqueteTotalCount, messageSendCountRows, messageSendCountTotal, messageSendCountPagingNo, messageSendCountLimitCount, messageSendCountSortBy, enqueteLoading, messageSendCountLoading, dateTimeRange, interval, value1, value2, isBeginAtZero, isPublicAnalyticsPage, isPublicAnalyticsApi, loading, labels, analytics, entryCount, applicantCount, newApplicantCount, winnerCount, followerUpDown, metricOptions, intervals, campaignName, campaignStartDatetime, campaignEndDatetime, isCampaignFinalCollection, isCampaignCollectionCompleted, collectCompletedAt, hasAnalyticsData, chartData, publicPageUrl, publicApiUrl, open, close, handleBeforeClose, handleDateRangeChange, handlePrizeChange, refreshEnqueteByTab, handleEnqueteQuestionChange, handleMessageSendCountsSort, handleMessageSendCountsPaging, handleMetricChange, handleIntervalChange, handlePublicFlagsChange, downloadCsv, downloadMessageSendCountsCsv, copyToClipboard, alert, handleBeginAtZeroChange, AnalyticsChartVertical, AnalyticsPeriodPicker, AnalyticsTotal, Icon, CsvDownload, InstagramInstantwinCampaignAnalyticsEnqueteTab, InstagramInstantwinCampaignAnalyticsMessageSendCountsTab }
}

})